import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import LoadingSpinner from '../../../components/LoadingSpinner/LoadingSpinner';
import NoItemsFound from '../../../components/NoItemsFound/NoItemsFound';
import TableHeader from '../../../components/Common/TableHeader';
import { TokenCookieManager } from '../../../utils/tokenCookies';
import { OrderDetailsModel } from '../../../interfaces/order.interface';
import OrderDetailsTable from '../PhysicalBookOrder/components/OrderDetailsTable';
import { AdminNavHandler } from '../adminNav';
import { fetchOrphanedOrders, OrphanBucket, OrphanRow, OrphanedOrdersResult } from './services/OrderRecovery.service';
import FulfillmentStatusBadges, { Badge } from '../PhysicalBookOrder/components/FulfillmentStatusBadges';
import { orderTypeLabel } from '../PhysicalBookOrder/helpers/orderDisplay';
import RefreshButton from '../../../components/Admin/RefreshButton';

interface OrderRecoveryProps {
  onOptionClick?: AdminNavHandler;
}

const TAB_META: Record<OrphanBucket, { label: string; blurb: string }> = {
  failed: {
    label: 'Failed',
    blurb:
      'Authorized and print-ready, but never reached a provider — these should have shipped. The real recovery list. "Error" means a send was attempted and failed; otherwise it failed silently. Open → Re-push (regenerate the PDF first if it has none).',
  },
  awaiting: {
    label: 'Awaiting shipment',
    blurb:
      'Not shipped yet — legitimately. Either pending the teacher to authorize shipping, or the book isn’t print-ready (the same gate the teacher ship flow uses). See the Reason column. No action needed; they ship once authorized / finished.',
  },
  missing_address: {
    label: 'Missing address',
    blurb: 'Released but has no shipping address — add an address before re-pushing.',
  },
};

const fmtDate = (iso: string | null): string => (iso ? new Date(iso).toLocaleDateString() : '-');

// Newest first.
const byNewest = (rows: OrphanRow[]): OrphanRow[] =>
  [...rows].sort((a, b) => (b.createdAt ? Date.parse(b.createdAt) : 0) - (a.createdAt ? Date.parse(a.createdAt) : 0));

const OrderRecovery: React.FC<OrderRecoveryProps> = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<OrphanedOrdersResult | null>(null);
  const [tab, setTab] = useState<OrphanBucket>('failed');
  const [emailFilter, setEmailFilter] = useState('');
  const [includeLegacy, setIncludeLegacy] = useState(false);
  const [selected, setSelected] = useState<{ order: OrderDetailsModel; email: string | null } | null>(null);

  useEffect(() => {
    if (!TokenCookieManager.hasToken()) navigate('/admin');
  });

  const load = async (email?: string, legacy: boolean = includeLegacy) => {
    setLoading(true);
    try {
      const result = await fetchOrphanedOrders({ email: email || undefined, includeLegacy: legacy });
      setData(result);
    } finally {
      setLoading(false);
    }
  };

  // Auto-scan all teachers on first open.
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const counts: Record<OrphanBucket, number> = {
    failed: data?.failed.length ?? 0,
    awaiting: data?.awaiting.length ?? 0,
    missing_address: data?.missingAddress.length ?? 0,
  };

  const rowsForTab = (): OrphanRow[] => {
    if (!data) return [];
    if (tab === 'failed') return byNewest(data.failed);
    if (tab === 'awaiting') return byNewest(data.awaiting);
    return byNewest(data.missingAddress);
  };

  // --- single-order recovery view (reuses the existing card) ---
  if (selected) {
    return (
      <div className="p-4">
        <button type="button" className="text-storyblue underline text-sm mb-3" onClick={() => setSelected(null)}>
          ← Back to list
        </button>
        <p className="mb-3 text-sm text-gray-600">Owner: {selected.email || '—'}</p>
        <OrderDetailsTable selectedOrderDetails={selected.order} email={selected.email} />
      </div>
    );
  }

  const rows = rowsForTab();

  return (
    <div className="p-4">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <input
          type="text"
          value={emailFilter}
          onChange={(e) => setEmailFilter(e.target.value)}
          placeholder="Filter by teacher email (optional)"
          className="border border-gray-300 rounded-md px-3 py-2 text-sm w-72"
          onKeyDown={(e) => {
            if (e.key === 'Enter') load(emailFilter.trim() || undefined);
          }}
        />
        <button
          type="button"
          className="bg-storyblue text-white font-semibold rounded-md px-4 py-2 text-sm"
          onClick={() => load(emailFilter.trim() || undefined)}
        >
          Scan
        </button>
        {emailFilter && (
          <button
            type="button"
            className="bg-gray-200 text-gray-700 font-semibold rounded-md px-4 py-2 text-sm"
            onClick={() => {
              setEmailFilter('');
              load();
            }}
          >
            Scan all teachers
          </button>
        )}
        {data?.trackFrom && (
          <label
            className="flex items-center gap-1 text-xs text-gray-500 cursor-pointer select-none"
            title={`Orders placed before ${data.trackFrom} are hidden by default`}
          >
            <input
              type="checkbox"
              checked={includeLegacy}
              onChange={(e) => {
                setIncludeLegacy(e.target.checked);
                load(emailFilter.trim() || undefined, e.target.checked);
              }}
            />
            Include legacy (before {data.trackFrom})
          </label>
        )}
        <RefreshButton onClick={() => load(emailFilter.trim() || undefined)} loading={loading} className="ml-auto" />
      </div>

      {loading ? (
        <LoadingSpinner />
      ) : !data ? (
        <NoItemsFound message="Could not load orders" />
      ) : (
        <>
          {/* Summary */}
          <div className="mb-4 text-sm text-gray-700">
            Scanned <b>{data.scanned.toLocaleString()}</b> orders
            {data.truncated ? ' (scan truncated — narrow with an email filter)' : ''}. Failed (should have shipped):{' '}
            <b>{counts.failed}</b> · Awaiting shipment: <b>{counts.awaiting}</b> (
            {data.summary.awaitingTeacherAuth} pending teacher auth, {data.summary.awaitingBookNotReady} book not ready) ·
            Missing address: <b>{counts.missing_address}</b>.
            {data.bundles.length > 0 && (
              <span> · {data.bundles.length} bulk/combined order(s) with failed children.</span>
            )}
          </div>

          {/* Tabs */}
          <div className="flex flex-wrap gap-1 mb-3 border-b border-gray-200">
            {(['awaiting', 'failed', 'missing_address'] as OrphanBucket[]).map((t) => (
              <button
                key={t}
                type="button"
                onClick={() => setTab(t)}
                className={`px-4 py-2 text-sm font-semibold -mb-px border-b-2 transition-colors ${
                  tab === t
                    ? 'border-storyblue text-storyblue'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {TAB_META[t].label}
                <span
                  className={`ml-2 rounded-full px-1.5 py-0.5 text-xs font-semibold ${
                    tab === t ? 'bg-storyblue/10 text-storyblue' : 'bg-gray-100 text-gray-500'
                  }`}
                >
                  {counts[t]}
                </span>
              </button>
            ))}
          </div>

          <p className="text-xs text-gray-500 mb-3">{TAB_META[tab].blurb}</p>

          {rows.length === 0 ? (
            <NoItemsFound message={`No ${TAB_META[tab].label.toLowerCase()} orders`} />
          ) : (
            <div className="w-full overflow-auto">
              <table className="min-w-full border border-gray-300">
                {tab === 'awaiting'
                  ? TableHeader(['Date', 'Owner', 'Order #', 'Type', 'Country', 'Reason', 'Age (d)', 'Bundle', ' '])
                  : tab === 'failed'
                    ? TableHeader(['Date', 'Owner', 'Order #', 'Type', 'Country', 'Status', 'Error', 'Age (d)', 'Bundle', ' '])
                    : TableHeader(['Date', 'Owner', 'Order #', 'Type', 'Country', 'Status', 'Age (d)', ' '])}
                <tbody>
                  {rows.map((r, index) => (
                    <tr key={`${r.orderNumber}-${index}`} className={index % 2 === 0 ? 'bg-gray-100' : ''}>
                      <td className="py-2 text-left px-4 border-b">{fmtDate(r.createdAt)}</td>
                      <td className="py-2 text-left px-4 border-b max-w-xs truncate" title={r.ownerEmail || ''}>
                        {r.ownerEmail || '—'}
                      </td>
                      <td className="py-2 text-left px-4 border-b">{r.orderNumber || '—'}</td>
                      <td className="py-2 text-left px-4 border-b">{orderTypeLabel(r.orderType)}</td>
                      <td className="py-2 text-left px-4 border-b">{r.shippingCountry || '—'}</td>
                      {tab === 'awaiting' ? (
                        <>
                          <td className="py-2 text-left px-4 border-b">
                            <Badge tone={r.reason === 'Pending teacher authorization' ? 'blue' : 'gray'}>
                              {r.reason || 'Awaiting'}
                            </Badge>
                          </td>
                          <td className="py-2 text-left px-4 border-b">{r.ageDays != null ? r.ageDays : '—'}</td>
                          <td className="py-2 text-left px-4 border-b">{r.parentBulkOrderNumber || '—'}</td>
                        </>
                      ) : tab === 'failed' ? (
                        <>
                          <td className="py-2 text-left px-4 border-b">
                            <FulfillmentStatusBadges status={r.fulfillmentStatus} actionNeeded={r.actionNeeded} />
                          </td>
                          <td className="py-2 text-left px-4 border-b max-w-xs truncate" title={r.lastFulfillmentError || undefined}>
                            {r.lastFulfillmentError ? (
                              <Badge tone="red" title={r.lastFulfillmentError}>
                                {r.lastFulfillmentError.slice(0, 40)}
                              </Badge>
                            ) : (
                              <span className="text-gray-400">not synced</span>
                            )}
                          </td>
                          <td className="py-2 text-left px-4 border-b">{r.ageDays != null ? r.ageDays : '—'}</td>
                          <td className="py-2 text-left px-4 border-b">{r.parentBulkOrderNumber || '—'}</td>
                        </>
                      ) : (
                        <>
                          <td className="py-2 text-left px-4 border-b">
                            <FulfillmentStatusBadges status={r.fulfillmentStatus} actionNeeded={r.actionNeeded} />
                          </td>
                          <td className="py-2 text-left px-4 border-b">{r.ageDays != null ? r.ageDays : '—'}</td>
                        </>
                      )}
                      <td className="py-2 text-center px-4 border-b">
                        <button
                          type="button"
                          className="bg-storygreen text-white font-semibold rounded-md px-3 py-1 text-xs"
                          title={r.recommendedAction}
                          onClick={() => setSelected({ order: r.order, email: r.ownerEmail })}
                        >
                          Open
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default OrderRecovery;
